import { endOfDay, isWithinInterval, startOfDay } from 'date-fns';

import { type AkaVeranstaltungId, Location, type Raum, type Zeitraum } from '../../../../../dtos';
import { getIntervalAlignedToStartAndEndOfDay } from '../../../../../utils';
import { type CalendarEvent, CalendarRaumEvent } from '../CalendarEvent';
import { calendarResourceTypes } from '../calendarResourceTypes';
import { SuggestedEvents } from './AbstractCalendarResource.js';
import { eventCreationStrategies, type EventCreationStrategy } from './AbstractCalendarResource.types.js';
import { AbstractRaumResource } from './AbstractRaumResource';

export class CalendarGruppenraumResource extends AbstractRaumResource {
  public readonly type = calendarResourceTypes.GRUPPENRAUM;

  public readonly id: `gr-${string}`;

  public readonly name: string;

  public readonly eventCreationStrategy: EventCreationStrategy =
    eventCreationStrategies.SEPARATE_EVENTS_FOR_ALL_ABLAUFTAGE;

  public constructor(raum: Raum, location: Location) {
    super(raum, location);
    this.id = `gr-${raum.raumSapId}`;
    this.name = `${raum.kuerzel} / ${raum.name}`;
  }

  public createPendingEventsFromBlockungen(akaVeranstaltungId: AkaVeranstaltungId): CalendarEvent[] {
    const events: CalendarRaumEvent[] = [];

    for (const blockung of this.raum.raumBlockungen.filter(
      (_blockung) => _blockung.akaVeranstaltungId === akaVeranstaltungId,
    )) {
      for (const interval of blockung.zeitraeume) {
        const newEvent = this.createEventFromBlockungAndInterval(blockung, interval);
        newEvent.isCurrentSelection = true;
        newEvent.resize = false;
        newEvent.editable = true;
        events.push(newEvent);
      }
    }

    return events;
  }

  public createPendingEventsFromSuggestions(
    suggestedEvents: SuggestedEvents,
    akaVeranstaltungId: AkaVeranstaltungId | null,
  ) {
    const tageWithGruppenraum = suggestedEvents.tage.filter((t) => t.gruppenraumSapIds?.includes(this.raum.raumSapId));

    return tageWithGruppenraum.map((t) => {
      const newEvent = new CalendarRaumEvent(t.zeitraum.start, t.zeitraum.end, this, {
        veranstaltung: null,
        quelleTerminId: akaVeranstaltungId ?? '',
        zeitraeume: [t.zeitraum],
      });

      newEvent.resize = false;
      newEvent.isOldSelection = false;
      newEvent.isCurrentSelection = true;
      newEvent.editable = true;

      return newEvent;
    });
  }

  protected computeNewCalendarEventBounds(date: Date): Zeitraum {
    const start = startOfDay(date);
    const end = endOfDay(date);

    return { start, end };
  }

  public isBookableAt(date: Date): boolean {
    return !this.raum.raumBlockungen.some((blockung) =>
      blockung.zeitraeume.some((zeitraum) => isWithinInterval(date, getIntervalAlignedToStartAndEndOfDay(zeitraum))),
    );
  }
}
